import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environmentBase } from '../environments/environment.base';
import { PortalFeatureFlag } from './portal-feature-flag.model';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly http = inject(HttpClient);
  private readonly flags = signal<Record<string, boolean>>({});

  /**
   * Fetches feature flags for the given portal from the API Gateway.
   * Called at app startup via provideFeatureFlags(), and again after Auth0
   * callback (initializer skips /callback because there is no token yet).
   */
  load(portalKey: string): Observable<void> {
    return this.http
      .get<PortalFeatureFlag[]>(`${environmentBase.apiUrl}/portals/${portalKey}/feature-flags`)
      .pipe(
        tap(flags => {
          const next: Record<string, boolean> = {};
          flags.forEach(f => (next[f.featureName] = f.isEnabled));
          this.flags.set(next);
        }),
        map(() => void 0),
        catchError(err => {
          console.warn('[FeatureFlagService] Failed to load feature flags:', err);
          return of(void 0);
        })
      );
  }

  /**
   * Returns true when the feature flag is enabled.
   * Reads a signal — safe inside computed() so the UI updates when flags load.
   */
  isEnabled(featureName: string): boolean {
    return this.flags()[featureName] ?? false;
  }
}
